import * as React from 'react';
import { FormGroup, Input } from 'reactstrap';
import { DropdownOption } from 'shared-client/types/fields';
import { FilterOptions } from '..';
import { StringArrayFilterEditProps } from '../providers/MultipleValue';
import FilterEditRequiredValidation from './FilterEditRequiredValidation';
import { FiltersListButtonGroup, FiltersListCloseButton, FiltersListAddButton, FiltersListValueButton } from './index';

interface MultipleDropDownFilterEditState {
	currentValue: string;
	displayValidationError: boolean;
}

export type MultipleDropDownFilterEditProps = StringArrayFilterEditProps & {
	dropdownValues: DropdownOption[];
};

/**
 * Configures multiple simple text values within a filter
 */
class MultipleDropDownFilterEdit extends React.Component<
	MultipleDropDownFilterEditProps,
	MultipleDropDownFilterEditState
> {
	public static defaultProps = {
		value: [],
		enumValues: [],
	};

	constructor(props) {
		super(props);

		this.state = {
			currentValue: '',
			displayValidationError: false,
		};
	}

	/**
	 * Update local state when the controlled text box is updated
	 *
	 * @private
	 * @memberof MultipleDropDownFilterEdit
	 */
	private handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ currentValue: e.currentTarget.value });
	};

	/**
	 *
	 * @private
	 * @returns
	 * @memberof MultipleDropDownFilterEdit
	 */
	private getValuesFromProps(): string[] {
		if (!this.props.value) {
			return [];
		}

		return this.props.value.filter((value) => !!value);
	}

	/**
	 * Displays the text of the dropdown where possible. In case it's not, falls back to display the value.
	 *
	 * @private
	 * @memberof MultipleDropDownFilterEdit
	 */
	private getFriendlyValueForDisplay = (value: any, filterOptions?: FilterOptions): React.ReactNode => {
		if (filterOptions != null && filterOptions.dropdownValues != null) {
			const result = filterOptions.dropdownValues.find((dropdownValue) => dropdownValue.value === value);
			return result ? result.text : value;
		} else {
			return value;
		}
	};

	/**
	 * Adds the value currently in state to the list of values selected.
	 *
	 * @private
	 * @memberof MultipleDropDownFilterEdit
	 */
	private addCurrentValue = () => {
		const trimmedValue = this.state.currentValue.trim();
		const values = this.getValuesFromProps();

		const validationError = !this.props.filter.nullable && !trimmedValue;

		this.setState({
			displayValidationError: validationError,
		});

		// ensure uniqueness
		if (!validationError && !values.includes(trimmedValue)) {
			// let the parent component know what the new values are.
			this.props.onChange([...values, trimmedValue], false);
		}

		this.setState({ currentValue: '' });
	};

	/**
	 * Allow users to hit enter to add new values.
	 *
	 * @private
	 * @memberof MultipleDropDownFilterEdit
	 */
	private handleOnKeyDown = (e: React.KeyboardEvent) => {
		if (e.which === 13) {
			e.preventDefault();
			this.addCurrentValue();
		}
	};

	/**
	 * Remove a specific value from the list
	 *
	 * @private
	 * @memberof MultipleDropDownFilterEdit
	 */
	private removeValue = (fieldIndex: number) => {
		const values = this.getValuesFromProps();

		// let the parent component know what the new values are.
		this.props.onChange(
			values.filter((_, index) => index !== fieldIndex),
			false,
		);
	};

	public render() {
		const availableEnumValues = this.props.dropdownValues.filter(
			(enumValue) => !this.props.value.includes(enumValue.value.toString()),
		);
		const values = this.getValuesFromProps();
		const filterActionClass =
			'filters-popover__actions ' + (values.length > 0 ? 'filters-popover__actions--divider' : '');

		return (
			<FormGroup>
				{availableEnumValues.length > 0 ? (
					<>
						<Input
							id="fieldValue"
							type="select"
							autoFocus
							value={this.state.currentValue}
							onKeyDown={this.handleOnKeyDown}
							onChange={this.handleOnChange}
							className=""
							autoComplete="off"
						>
							<option value="" />
							{availableEnumValues.map((status, index) => {
								return (
									<option key={index} value={status.value}>
										{status.text}
									</option>
								);
							})}
						</Input>
						<div className={filterActionClass}>
							<FiltersListAddButton onClick={this.addCurrentValue} />
						</div>
					</>
				) : (
					<p>No more values available.</p>
				)}
				{this.state.displayValidationError ? (
					<FilterEditRequiredValidation messageOverride="Field is required" />
				) : null}

				{values.map((value, index) => {
					return (
						<React.Fragment key={index}>
							<FiltersListButtonGroup>
								<FiltersListValueButton>
									{this.getFriendlyValueForDisplay(value, this.props.filter.options)}
								</FiltersListValueButton>
								<FiltersListCloseButton
									onClick={() => {
										this.removeValue(index);
									}}
								/>
							</FiltersListButtonGroup>{' '}
						</React.Fragment>
					);
				})}
				<p />
			</FormGroup>
		);
	}
}

export default MultipleDropDownFilterEdit;
